/**
 * MarkdownRenderer
 * 目的: ブログ本文をMarkdownとして安全にレンダリングする（装飾/リンク/画像に対応）
 *
 * 引数:
 * - content: Markdown本文（DBの posts.body を想定）
 *
 * 戻り値:
 * - ReactNode
 *
 * 想定される例外:
 * - なし（レンダリング中の例外はReactのエラーバウンダリに委ねる）
 */

import React from 'react';
import Image from 'next/image';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeSanitize, { defaultSchema } from 'rehype-sanitize';
import rehypeExternalLinks from 'rehype-external-links';

type Props = {
  content: string;
  className?: string;
};

/**
 * Markdownのサニタイズ設定
 * 目的: XSSを防ぎつつ、ブログ用途のタグ（a/img/見出し/強調など）を許可する
 */
const sanitizeSchema = {
  ...defaultSchema,
  attributes: {
    ...(defaultSchema.attributes ?? {}),
    a: [
      ...(defaultSchema.attributes?.a ?? []),
      'href',
      'title',
      'target',
      'rel',
    ],
    img: [
      ...(defaultSchema.attributes?.img ?? []),
      'src',
      'alt',
      'title',
      'width',
      'height',
      'loading',
    ],
    code: [...(defaultSchema.attributes?.code ?? []), 'className'],
    span: [...(defaultSchema.attributes?.span ?? []), 'className'],
  },
  protocols: {
    ...(defaultSchema.protocols ?? {}),
    href: ['http', 'https', 'mailto', 'tel'],
    src: ['http', 'https'],
  },
};

/**
 * Markdownリンク・画像の最低限バリデーション
 * 目的: サニタイズ外の追加防御（相対URL/不正スキームを避ける）
 */
function isSafeUrl(url: string): boolean {
  try {
    // 相対パスも許可（/assets/... など）
    if (url.startsWith('/')) return true;
    const u = new URL(url);
    return u.protocol === 'http:' || u.protocol === 'https:';
  } catch {
    return false;
  }
}

/**
 * Markdownから渡ってくる width/height 属性を数値に変換する
 * 目的: next/image に渡すため（無効値は undefined に落とす）
 *
 * @param v unknown
 * @return number | undefined
 *
 * 想定される例外:
 * - なし（パース失敗は undefined）
 */
function parseHtmlDimension(v: unknown): number | undefined {
  if (typeof v === 'number' && Number.isFinite(v) && v > 0) return v;
  if (typeof v === 'string') {
    const n = Number(v);
    if (Number.isFinite(n) && n > 0) return n;
  }
  return undefined;
}

export function MarkdownRenderer({ content, className }: Props) {
  const safeContent = content ?? '';

  return (
    <div className={className}>
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        rehypePlugins={[
          // 外部リンクは新規タブ+安全なrelを付与
          [
            rehypeExternalLinks,
            { target: '_blank', rel: ['noopener', 'noreferrer'] },
          ],
          // HTMLはサニタイズ（XSS防止）
          [rehypeSanitize, sanitizeSchema as any],
        ]}
        // 見た目を既存デザインに寄せるために要素ごとにクラス付与
        components={{
          h1: ({ children, ...props }) => (
            <h1
              className='mt-8 font-serif text-2xl text-gray-800 md:text-3xl'
              {...props}
            >
              {children}
            </h1>
          ),
          h2: ({ children, ...props }) => (
            <h2 className='mt-8 font-serif text-xl text-gray-800' {...props}>
              {children}
            </h2>
          ),
          h3: ({ children, ...props }) => (
            <h3 className='mt-6 font-serif text-lg text-gray-800' {...props}>
              {children}
            </h3>
          ),
          p: ({ children, ...props }) => (
            <p className='mt-3 text-[15px] leading-7 text-gray-700/90' {...props}>
              {children}
            </p>
          ),
          ul: ({ children, ...props }) => (
            <ul className='my-4 list-disc space-y-1 pl-5' {...props}>
              {children}
            </ul>
          ),
          ol: ({ children, ...props }) => (
            <ol className='my-4 list-decimal space-y-1 pl-5' {...props}>
              {children}
            </ol>
          ),
          li: ({ children, ...props }) => (
            <li className='text-[15px] leading-7 text-gray-700/90' {...props}>
              {children}
            </li>
          ),
          blockquote: ({ children, ...props }) => (
            <blockquote
              className='my-6 border-l-4 border-emerald-200 bg-emerald-50/40 px-4 py-3 text-gray-700'
              {...props}
            >
              {children}
            </blockquote>
          ),
          a: ({ children, href, ...props }) => {
            const safeHref = href && isSafeUrl(href) ? href : undefined;
            return (
              <a
                href={safeHref}
                className='text-emerald-700 underline underline-offset-2 hover:text-emerald-800'
                {...props}
              >
                {children}
              </a>
            );
          },
          img: ({ src, alt, ...props }) => {
            // 想定される例外:
            // - react-markdown の型定義上、imgのsrcが string 以外（Blob等）になる場合がある
            // - その場合はURL検証ができないため表示しない（安全側に倒す）
            const safeSrc =
              typeof src === 'string' && isSafeUrl(src) ? src : undefined;
            const width = parseHtmlDimension((props as any).width);
            const height = parseHtmlDimension((props as any).height);
            const ratio =
              width && height ? `${width} / ${height}` : '16 / 9';
            return (
              // 目的: ブログ本文の画像も next/image で最適化する（レスポンシブ配信/圧縮）
              // NOTE: width/height が無い場合は aspect-ratio を仮定し fill で表示する
              <span
                className='relative my-6 block w-full overflow-hidden rounded-sm border border-gray-100 bg-gray-50'
                style={{ aspectRatio: ratio }}
              >
                {safeSrc ? (
                  <Image
                    src={safeSrc}
                    alt={alt ?? ''}
                    fill
                    sizes='(min-width: 768px) 720px, 100vw'
                    quality={90}
                    className='object-contain'
                  />
                ) : null}
              </span>
            );
          },
          hr: (props) => <hr className='my-8 border-gray-200' {...props} />,
          code: ({ children, className, ...props }) => (
            <code
              className={[
                'rounded bg-gray-100 px-1 py-0.5 font-mono text-[0.95em] text-gray-800',
                className ?? '',
              ].join(' ')}
              {...props}
            >
              {children}
            </code>
          ),
        }}
      >
        {safeContent}
      </ReactMarkdown>
    </div>
  );
}


